"use client";

import { useCallback, useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { useIsHydrated } from "@/hooks/useIsHydrated";
import { useWebPushSubscription } from "@/lib/hooks/useWebPushSubscription";
import {
  PUSH_NOTIFICATIONS_HELP_HREF,
  pushPermissionHelpHint,
} from "@/lib/notifications/push-settings-help";

const PWA_POST_INSTALL_PUSH_FLAG = "sell-it-off:pwa-post-install-push-pending";

export function markPwaPostInstallPushPending(): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(PWA_POST_INSTALL_PUSH_FLAG, "1");
}

export function isPwaPostInstallPushPending(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(PWA_POST_INSTALL_PUSH_FLAG) === "1";
}

function clearPwaPostInstallPushPending(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(PWA_POST_INSTALL_PUSH_FLAG);
}

export default function PwaPostInstallPushPrompt() {
  const webPush = useWebPushSubscription();
  const hydrated = useIsHydrated();
  const [dismissed, setDismissed] = useState(false);
  const open = hydrated && !dismissed && isPwaPostInstallPushPending();

  const dismiss = useCallback(() => {
    clearPwaPostInstallPushPending();
    setDismissed(true);
  }, []);

  const handleEnablePush = async () => {
    await webPush.enablePush({ source: "pwa_post_install" });
    dismiss();
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={dismiss}
      title="Local alerts on this device"
      maxWidth="sm"
    >
      <div className="space-y-4 text-sm text-text-light">
        <p>
          By default, new item notifications are sent to your app inbox and
          email. Enable instant notifications on the app from this browser.
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={dismiss}>
            Maybe later
          </Button>
          <Button
            type="button"
            onClick={() => void handleEnablePush()}
            disabled={
              webPush.busy ||
              !webPush.isSupported ||
              !webPush.vapidConfigured ||
              webPush.permission === "denied"
            }
          >
            Enable notifications
          </Button>
        </div>
        {webPush.permission === "denied" ? (
          <p className="text-xs text-amber-800">
            Notifications are blocked in your browser settings.{" "}
            <a
              href={PUSH_NOTIFICATIONS_HELP_HREF}
              className="font-medium text-primary hover:underline"
            >
              {pushPermissionHelpHint()}
            </a>
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
